import { Menu, Tooltip } from '@pique/component';
import type { ActionItemGroupBy } from '@pique/core/modules/properties/services/schema';
import { useCallback, useState } from 'react';

import * as styles from './CompletionFilter.css';

export type CompletionFilterType = 'open' | 'closed' | 'all';
export type ActionItemsListGroupBy = Extract<
  ActionItemGroupBy,
  'pageId' | 'dueDate'
>;

const GroupByIcon = ({ size = 14 }: { size?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4.125 6.25C3.575 6.25 3.125 6.7 3.125 7.25C3.125 7.8 3.575 8.25 4.125 8.25H4.13501C4.68501 8.25 5.13501 7.8 5.13501 7.25C5.13501 6.7 4.68501 6.25 4.13501 6.25H4.125ZM8.125 6.5C7.715 6.5 7.375 6.84 7.375 7.25C7.375 7.66 7.715 8 8.125 8H20.125C20.535 8 20.875 7.66 20.875 7.25C20.875 6.84 20.535 6.5 20.125 6.5H8.125ZM7.125 12.25C7.125 11.7 7.575 11.25 8.125 11.25H8.13501C8.68501 11.25 9.13501 11.7 9.13501 12.25C9.13501 12.8 8.68501 13.25 8.13501 13.25H8.125C7.575 13.25 7.125 12.8 7.125 12.25ZM8.125 16.25C7.575 16.25 7.125 16.7 7.125 17.25C7.125 17.8 7.575 18.25 8.125 18.25H8.13501C8.68501 18.25 9.13501 17.8 9.13501 17.25C9.13501 16.7 8.68501 16.25 8.13501 16.25H8.125ZM11.375 12.25C11.375 11.84 11.715 11.5 12.125 11.5H20.125C20.535 11.5 20.875 11.84 20.875 12.25C20.875 12.66 20.535 13 20.125 13H12.125C11.715 13 11.375 12.66 11.375 12.25ZM12.125 16.5C11.715 16.5 11.375 16.84 11.375 17.25C11.375 17.66 11.715 18 12.125 18H20.125C20.535 18 20.875 17.66 20.875 17.25C20.875 16.84 20.535 16.5 20.125 16.5H12.125Z"
      fill="currentColor"
    />
  </svg>
);

interface CompletionFilterProps {
  selectedFilter: CompletionFilterType;
  onFilterChange: (filter: CompletionFilterType) => void;
  openCount: number;
  closedCount: number;
  totalCount: number;
  groupBy?: ActionItemsListGroupBy;
  onGroupByChange?: (groupBy: ActionItemsListGroupBy) => void;
}

export const CompletionFilter: React.FC<CompletionFilterProps> = ({
  selectedFilter,
  onFilterChange,
  openCount,
  closedCount,
  totalCount,
  groupBy = 'pageId',
  onGroupByChange,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleFilterClick = useCallback(
    (filter: CompletionFilterType) => {
      onFilterChange(filter);
    },
    [onFilterChange]
  );

  const handleGroupByChange = useCallback(
    (next: ActionItemsListGroupBy) => {
      onGroupByChange?.(next);
      setMenuOpen(false);
    },
    [onGroupByChange]
  );

  const getFilterCount = (filter: CompletionFilterType): number => {
    switch (filter) {
      case 'open':
        return openCount;
      case 'closed':
        return closedCount;
      case 'all':
        return totalCount;
      default:
        return 0;
    }
  };

  const getFilterLabel = (filter: CompletionFilterType): string => {
    switch (filter) {
      case 'open':
        return 'To-Do';
      case 'closed':
        return 'Done';
      case 'all':
        return 'All';
      default:
        return '';
    }
  };

  const filters: CompletionFilterType[] = ['open', 'closed', 'all'];

  return (
    <div className={styles.completionFilterContainer}>
      {onGroupByChange ? (
        <div className={styles.sortMenuAnchor}>
          <Menu
            rootOptions={{ open: menuOpen, onOpenChange: setMenuOpen }}
            contentOptions={{ align: 'start', sideOffset: 6 }}
            items={
              <div className={styles.sortMenuContent}>
                <button
                  type="button"
                  className={`${styles.sortMenuItem} ${
                    groupBy === 'pageId' ? styles.sortMenuItemActive : ''
                  }`}
                  onClick={() => handleGroupByChange('pageId')}
                >
                  By meeting
                </button>
                <button
                  type="button"
                  className={`${styles.sortMenuItem} ${
                    groupBy === 'dueDate' ? styles.sortMenuItemActive : ''
                  }`}
                  onClick={() => handleGroupByChange('dueDate')}
                >
                  By due date
                </button>
              </div>
            }
          >
            <span>
              <Tooltip
                content="Group by"
                side="left"
                rootOptions={{ open: menuOpen ? false : undefined }}
              >
                <button
                  type="button"
                  className={styles.sortMenuButton}
                  data-open={menuOpen || undefined}
                  data-active={groupBy !== 'pageId' || undefined}
                  aria-label="Group action items by"
                >
                  <GroupByIcon size={14} />
                </button>
              </Tooltip>
            </span>
          </Menu>
        </div>
      ) : null}
      {filters.map(filter => (
        <button
          key={filter}
          className={`${styles.filterButton} ${
            selectedFilter === filter ? styles.filterButtonSelected : ''
          }`}
          onClick={() => handleFilterClick(filter)}
        >
          {getFilterLabel(filter)} ({getFilterCount(filter)})
        </button>
      ))}
    </div>
  );
};
